'use client'

import { useCallback, useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useScrollLock } from '@/lib/ui/scroll-lock'
import { useModalLayer } from '@/lib/ui/modal-stack'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useMounted } from '@/lib/hooks/use-mounted'

// ============================================================================
// МОДАЛЬНОЕ ОКНО — ОДНО НА ПРОЕКТ
// ----------------------------------------------------------------------------
// ЧТО БЫЛО. 55 окон в 48 файлах, каждое собирало подложку, контейнер, заголовок
// и крестик заново. Замеры по коду: 6 разных ширин, 7 отступов, 8 размеров
// крестика (от 9 до 20 пикселей). Но главное не вид:
//
//   • закрытие по Escape было в 5 файлах из 48;
//   • прокрутка страницы под окном не блокировалась — фон уезжал под пальцем;
//   • фокус после закрытия терялся: Tab начинал с начала страницы;
//   • читалка экрана не знала, что открылось окно.
//
// Это чинится один раз здесь, а не сорок восемь раз по файлам.
//
// ЧЕГО ЗДЕСЬ НЕТ НАМЕРЕННО. Никакой анимации содержимого, никаких вложенных
// окон, никакого управления состоянием. Окно показывает то, что ему передали, и
// сообщает, что его закрыли.
// ============================================================================

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full'

/** Пометка заголовка окна, нарисованного не h1–h3: по нему `Overlay` называет окно. */
export const DIALOG_TITLE_ATTR = '[data-dialog-title]'

const SIZE: Record<ModalSize, string> = {
  sm: 'max-w-[400px]',   // подтверждение, короткий вопрос
  md: 'max-w-[520px]',   // форма на несколько полей
  lg: 'max-w-[720px]',   // таблица, список выбора
  xl: 'max-w-[960px]',   // подбор перевозчика, разбор импорта
  full: 'max-w-[min(1200px,95vw)]', // редакторы почти во весь экран
}

export function Modal({
  open,
  onClose,
  title,
  subtitle,
  size = 'md',
  footer,
  children,
  /**
   * Закрывать ли по клику на фон и по Escape.
   *
   * Выключается там, где в окне вводят данные и случайный клик мимо стоил бы
   * заполненной формы — импорт, редактор лендинга. По умолчанию включено:
   * запертое окно раздражает сильнее, чем потерянный черновик.
   */
  dismissable = true,
  closeLabel = 'Close',
  windowBar,
  className,
  tour,
}: {
  open: boolean
  onClose: () => void
  title?: ReactNode
  subtitle?: ReactNode
  size?: ModalSize
  footer?: ReactNode
  children: ReactNode
  dismissable?: boolean
  /**
   * Подпись крестика для читалки экрана. По умолчанию латиницей: компонент
   * общий и не тянет за собой словарь — иначе он стал бы зависеть от locale и
   * не работал бы в админке, где префикса локали нет. Локализованную подпись
   * передают снаружи те экраны, где это важно.
   */
  closeLabel?: string
  /**
   * Полоса широкого окна (WindowBar: «Поделиться · Во весь экран · ✕») —
   * над заголовком. С ней свой крестик заголовка не рисуется: он в полосе.
   */
  windowBar?: ReactNode
  className?: string
  /** Якорь обучения (data-tour) на панели окна, см. lib/tours/catalog.ts. */
  tour?: string
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  // Кто открыл окно — туда вернём фокус после закрытия.
  const openerRef = useRef<HTMLElement | null>(null)

  // Место в стопке. Отсюда и слой, и право слушать Escape: и то и другое
  // раньше было у всех окон одинаковым, из-за чего вложенное окно уезжало под
  // родителя, а один Escape закрывал оба.
  const layer = useModalLayer(open)
  const mounted = useMounted()
  // Своё имя у каждого окна: два окна в стопке с одним `id` заголовка
  // называли бы друг друга одним именем (jPpsc4wQ).
  const titleId = useId()

  const handleClose = useCallback(() => {
    if (dismissable) onClose()
  }, [dismissable, onClose])

  // ── Escape ────────────────────────────────────────────────────────────────
  // Слушает только ВЕРХНЕЕ окно.
  //
  // `stopPropagation` эту задачу не решал и решить не мог: он останавливает
  // всплытие к предкам, а обработчики висят на `document`, у которого предков
  // нет. Все они срабатывали на одно нажатие — и Escape закрывал вместе с
  // документом ещё и сделку, из которой его открыли.
  useEffect(() => {
    if (!open || !layer.isTop) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); handleClose() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, layer.isTop, handleClose])

  // ── Прокрутка страницы под окном ──────────────────────────────────────────
  // Без этого фон уезжает, когда крутишь колесо над окном, и после закрытия
  // человек оказывается не там, где был.
  useScrollLock(open)

  // ── Фокус ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    openerRef.current = document.activeElement as HTMLElement | null

    // Первый фокус — на само окно, а не на первую кнопку: иначе читалка
    // начинает с «Отмена», не прочитав заголовок.
    panelRef.current?.focus()

    return () => {
      // Возврат в следующем кадре: к этому моменту окно уже размонтировано,
      // иначе браузер снимет фокус обратно.
      const opener = openerRef.current
      if (opener && document.contains(opener)) {
        requestAnimationFrame(() => opener.focus())
      }
    }
  }, [open])

  // ── Фокус не уходит за пределы окна ───────────────────────────────────────
  // Ловит Tab тоже только верхнее: иначе два окна перехватывают его
  // одновременно и фокус мечется между ними.
  useEffect(() => {
    if (!open || !layer.isTop) return
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !panelRef.current) return
      const items = panelRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onTab)
    return () => document.removeEventListener('keydown', onTab)
  }, [open, layer.isTop])

  if (!open) return null
  // Портал — только после монтирования: сервер рисует null, и первый проход
  // в браузере обязан совпасть с ним, иначе React #418 у окна, открытого с загрузки.
  if (!mounted) return null

  return createPortal(
    <div
      // Слой — место в стопке, а не число, выбранное на глаз. Открыл окно из
      // окна — оно сверху, и никому не пришлось ничего подбирать.
      style={{ zIndex: layer.z }}
      className="fixed inset-0 flex items-center justify-center bg-ink-900/45 p-4 animate-fade-in"
      onClick={e => { if (e.target === e.currentTarget) handleClose() }}
    >
      <div
        ref={panelRef}
        data-tour={tour}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        className={cn(
          'w-full bg-white dark:bg-ink-800 rounded-[16px] shadow-xl overflow-hidden outline-none flex flex-col max-h-[90vh]',
          SIZE[size],
          className,
        )}
      >
        {windowBar}
        {(title || (dismissable && !windowBar)) && (
          <div className="flex items-start justify-between gap-3 px-5 py-4 border-b-hairline border-ink-100 dark:border-ink-700 shrink-0">
            <div className="min-w-0">
              {title && (
                <div id={titleId} className="text-[15px] font-semibold tracking-[-0.02em] text-ink-900 dark:text-ink-100">
                  {title}
                </div>
              )}
              {subtitle && <div className="text-[12.5px] text-ink-500 mt-0.5">{subtitle}</div>}
            </div>
            {dismissable && !windowBar && (
              <button
                type="button"
                onClick={onClose}
                aria-label={closeLabel}
                className="shrink-0 rounded-md p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-900 dark:hover:bg-ink-700 transition-colors"
              >
                <X size={16} strokeWidth={2} />
              </button>
            )}
          </div>
        )}

        <div className="px-5 py-[18px] overflow-y-auto flex-1">{children}</div>

        {footer && (
          <div className="flex justify-end gap-2.5 px-5 py-3.5 border-t-hairline border-ink-100 dark:border-ink-700 shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  )
}

// ============================================================================
// OVERLAY — подложка без начинки.
// ----------------------------------------------------------------------------
// Modal выше диктует и рамку окна: заголовок, крестик, футер. Восемьдесят с
// лишним готовых окон в проекте свою рамку уже нарисовали, и переписывать их
// под чужой API — это менять разметку там, где она работает, ради одного лишь
// поведения. Overlay даёт ровно поведение:
//
//   • портал в body — иначе `space-y-*` родителя вешает на `fixed inset-0`
//     свой margin-top, и подложка съезжает вниз, оставляя непокрытую полосу
//     сверху и незатемнённый сайдбар;
//   • закрытие по фону и по Escape;
//   • блокировка прокрутки фона;
//   • возврат фокуса тому, кто открыл.
//
// Замена на месте: <div className="fixed inset-0 …"> → <Overlay onClose={…}>.
// Панель внутри остаётся как есть.
// ============================================================================
export function Overlay({
  onClose,
  children,
  className,
  dismissable = true,
  role = 'dialog',
  labelledBy,
  label,
}: {
  onClose: () => void
  children: ReactNode
  /** Раскладка подложки, если нужна не по центру (например, окно сверху). */
  className?: string
  dismissable?: boolean
  /**
   * Окно поверх страницы — для диктора и тестов это окно (role="dialog"),
   * подтверждение — «alertdialog» (Z359LPw5). Прежде подложка была просто
   * блоком: карточку записи, подтверждение, «последствия» не видел никто.
   */
  role?: 'dialog' | 'alertdialog'
  /** Идентификатор заголовка окна — его название. */
  labelledBy?: string
  /** Название окна, если видимого заголовка нет. */
  label?: string
}) {
  const openerRef = useRef<HTMLElement | null>(null)
  const dialogRef = useRef<HTMLDivElement | null>(null)
  const autoTitleId = useId()

  // Подложка живёт, только пока смонтирована, — поэтому в стопке она всегда
  // «открыта».
  const layer = useModalLayer(true)
  const mounted = useMounted()

  const handleClose = useCallback(() => { if (dismissable) onClose() }, [dismissable, onClose])

  useEffect(() => {
    if (!layer.isTop) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); handleClose() }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [layer.isTop, handleClose])

  useScrollLock(true)

  useEffect(() => {
    openerRef.current = document.activeElement as HTMLElement | null
    return () => {
      const opener = openerRef.current
      if (opener && document.contains(opener)) requestAnimationFrame(() => opener.focus())
    }
  }, [])

  // ── НАЗВАНИЕ ОКНА — ЕГО ЗАГОЛОВОК (jPpsc4wQ) ─────────────────────────────
  // Название передавали только ConfirmDialog и ConsequenceDialog; остальные
  // 78 окон диктор читал как «диалог» без имени. Окно без `label` /
  // `labelledBy` называется само: по элементу с `data-dialog-title`, иначе по
  // первому заголовку h1–h3 внутри. Карточка рисует заголовок после загрузки
  // — наблюдатель подхватывает его, когда он появится. Вложенные окна живут в
  // своём портале, поэтому чужой заголовок сюда не попадает.
  useEffect(() => {
    const node = dialogRef.current
    if (!node || labelledBy || label) return
    const link = () => {
      const title = node.querySelector<HTMLElement>(DIALOG_TITLE_ATTR)
        ?? node.querySelector<HTMLElement>('h1, h2, h3')
      if (!title) { node.removeAttribute('aria-labelledby'); return }
      if (!title.id) title.id = autoTitleId
      if (node.getAttribute('aria-labelledby') !== title.id) node.setAttribute('aria-labelledby', title.id)
    }
    link()
    if (typeof MutationObserver === 'undefined') return
    const observer = new MutationObserver(link)
    observer.observe(node, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [mounted, labelledBy, label, autoTitleId])

  // Портал — только после монтирования (см. Modal): иначе React #418.
  if (!mounted) return null

  return createPortal(
    <div
      ref={dialogRef}
      role={role}
      aria-modal="true"
      aria-labelledby={labelledBy}
      aria-label={labelledBy ? undefined : label}
      style={{ zIndex: layer.z }}
      className={cn(
        'fixed inset-0 flex items-center justify-center bg-ink-900/45 p-4',
        className,
      )}
      onClick={e => { if (e.target === e.currentTarget) handleClose() }}
    >
      {children}
    </div>,
    document.body,
  )
}
